import { Button } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import Icon from "../../../Icon"
import { PresetDateRangeFilter } from "../../../components/Filters/PresetDateRangeFilter.tsx"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { cn } from "../../../utils/cn.ts"
import { clearAllFilters } from "../helpers/clear-all-filters.ts"
import { DEFAULT_LAYERS } from "../redux/global-map.constants.ts"
import {
  clearFilters,
  selectActiveFilters,
  selectActiveLayers,
  selectActiveTab,
  selectFacets,
  selectSearchTerm,
  setActiveLayers,
  setFilters
} from "../redux/global-map.slice"
import type { GlobalMapFilters } from "../redux/global-map.types"
import { FacetMultiSelectFilter } from "./FacetMultiSelectFilter.tsx"
import { LookAroundControl } from "./LookAroundControl.tsx"
import { hasAnyActiveFilter } from "./active-filter-summary.ts"
import {
  ACTIVE_PERIOD_WIDTH_CLASS,
  FILTER_PILL_CLASS
} from "./filter-pill.ts"

// No icon in `@ant-design/icons` reads as "clear/reset" the way an eraser
// does — per explicit product direction for the "Clear All" button — so
// this is a small custom glyph rather than a repurposed X/delete icon.
// Minimal by design: a tilted rounded rectangle with a center divider,
// enough to read as an eraser at the ~10px size a pill button icon needs,
// without the fine detail a literal eraser illustration would lose at that
// size. `currentColor` so it inherits the button's own text color in both
// its default and hover states.
function EraserIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <g transform="rotate(-45 8 8)">
        <rect
          x="3"
          y="5"
          width="10"
          height="6"
          rx="1.5"
          fill="currentColor"
          fillOpacity="0.25"
          stroke="currentColor"
          strokeWidth="1"
        />
        <path d="M3 8h10" stroke="currentColor" strokeWidth="1" />
      </g>
    </svg>
  )
}

// No named presets: unlike `ProjectFilters.created_window`, the backend's
// active-period contract (`active_period_start`/`active_period_end` only —
// see `global-map.api.ts`) has no preset-key param to resolve server-side, and
// this plan's own constraint forbids computing preset ranges client-side. So
// `presetKey` is aliased onto `afterKey` below: `PresetDateRangeFilter` always
// writes `presetKey` before `afterKey`/`beforeKey` in the same patch object
// (see its `emit`), and with an empty preset list it's always writing `null`
// there — a value `afterKey`'s own write for the same field immediately
// overwrites. That keeps every emitted patch to exactly the two real
// `GlobalMapFilters` fields this control owns.
const NO_ACTIVE_PERIOD_PRESETS: readonly { key: string; label: string }[] = []

// Landmine guard, not a real runtime scenario: the `presetKey`/`afterKey`
// alias above is only correct because this list is always empty. If a later
// task ever populates `NO_ACTIVE_PERIOD_PRESETS` at this call site — to offer
// real named presets — a preset click would emit `presetKey: option.key`
// and then have `afterKey`'s write to that same aliased field silently
// overwrite it, clearing the filter instead of applying the preset.
// TypeScript can't catch this (both keys are independently valid
// `keyof GlobalMapFilters`), and no existing test drives a non-empty
// `presets` array. This throws loudly the moment that happens so nobody
// ships that regression unnoticed — give `GlobalMapFilters` a real,
// dedicated preset-key field first, then delete both this guard and the
// alias.
if (NO_ACTIVE_PERIOD_PRESETS.length > 0) {
  throw new Error(
    "NO_ACTIVE_PERIOD_PRESETS must stay empty: GlobalMapFilterBar aliases " +
      'PresetDateRangeFilter\'s presetKey onto "activePeriodStart" (the same ' +
      "field as afterKey), which only stays correct while every preset write " +
      "is null. Add a dedicated preset-key field to GlobalMapFilters and stop " +
      "aliasing before populating this list."
  )
}

/**
 * The Global Map filter bar. Six pickers — Active Period / Salesperson /
 * Project Manager / Team Member / Subcontractor / Status / Project Type —
 * share one look: an icon-led "pill" trigger (`FILTER_PILL_CLASS`) that
 * opens a checkbox dropdown, and shows "Label (N)" once N options are
 * checked — per explicit product direction that every filter behave the
 * same way, not just status/project type. "Projects & Leads" is the one
 * exception (see below). `RelationshipFilter` (single-select) is retired;
 * the four relationship pickers now use the same `FacetMultiSelectFilter` status/project type
 * already used, over `GlobalMapFilters`' widened `salespersonIds`/
 * `projectManagerIds`/`teamMemberIds`/`subcontractorIds` array fields.
 * Every option list comes from `selectFacets`; when facets are `null`
 * (nothing has dispatched `fetchFacets` yet) a picker simply renders no
 * options rather than blocking on a fetch.
 *
 * The List tab's row is otherwise identical to the Map tab's — Active
 * Period / Salesperson / Project Manager / Team Member / Subcontractor /
 * Status / Project Type — no Region, no Search, no "More Filters" popover.
 * Region and the "More Filters" popover both existed at one point and were
 * removed per explicit product direction; the search box
 * (`GlobalRecordSearchInput`) renders once, above the map/list content, the
 * same place for both tabs (see `MapTab.tsx`'s own comment) — keeping this
 * row short enough that Clear All stays on the same line at normal widths.
 * `GlobalMapFilters.region` itself is untouched (still a real, url-synced
 * field) — only this row's picker for it is gone; `activeTab` is read from
 * the same `selectActiveTab` selector `GlobalMapToolbar` already uses to
 * gate its own Layers-button split — reused here rather than a second
 * mechanism.
 *
 * "Projects & Leads" (`LayerToggleButtons`) no longer renders here — a
 * narrow-viewport layout pass moved it out to sit beside the Map/List/
 * Analytics tabs instead (`GlobalMapToolbar`'s own second row), so this bar
 * stays a clean single group of pickers plus Clear All/View All, rather
 * than wrapping unpredictably with the tabs and everything else sharing one
 * row at laptop widths.
 *
 * "Clear All"/"View All" are the two controls still outside the main filter
 * group — a standalone pair at the row's own trailing end. Both now also
 * clear the unified search box's own committed state (`onClearSearch`/
 * `onViewAll`, both from `useMapFeatures` — `clearSearch`/`viewAll`
 * respectively) per explicit product direction: a lingering search term
 * must not keep narrowing the map after either button is pressed.
 * "Clear All" (disabled, not hidden, while no filter is active) only ever
 * clears filters/layers/search; "View All" always clears them too AND
 * explicitly hands the camera back to `MapTab`'s auto-fit — necessary
 * because `clearFilters` sets `committedQuery.filters` to the shared
 * `EMPTY_FILTERS` reference, so clicking it when filters are ALREADY empty
 * is a no-op by object identity and would never on its own re-trigger the
 * fetch/auto-fit effect that a real filter change relies on to reset
 * `userPanned`.
 */
export function GlobalMapFilterBar({
  onViewAll,
  onClearSearch
}: {
  onViewAll?: () => void
  onClearSearch?: () => void
}) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const filters = useAppSelector(selectActiveFilters)
  const facets = useAppSelector(selectFacets)
  const activeLayers = useAppSelector(selectActiveLayers)
  const searchTerm = useAppSelector(selectSearchTerm)
  // Reuses the exact same selector `GlobalMapToolbar` already reads to
  // highlight the active Map/List/Analytics tab — see this component's own
  // "List-tab-only" comments below for why this matters.
  const activeTab = useAppSelector(selectActiveTab)

  const patchFilters = (patch: Partial<GlobalMapFilters>) => {
    dispatch(setFilters(patch))
  }

  // Values arrive entity-tagged (`"project:1"`/`"lead:1"`, see
  // `FacetMultiSelectFilter`'s `optionSelectValue`) — split back into the
  // two separate `GlobalMapFilters` fields by prefix. An unrecognized/
  // untagged entry (shouldn't happen — every status facet option carries
  // `entity`) is dropped rather than guessed at.
  const handleStatusChange = (values: string[]) => {
    const projectStatusIds: number[] = []
    const leadStatusIds: number[] = []
    for (const value of values) {
      const [entity, rawId] = value.split(":")
      const id = Number(rawId)
      if (Number.isNaN(id)) continue
      if (entity === "project") projectStatusIds.push(id)
      else if (entity === "lead") leadStatusIds.push(id)
    }
    patchFilters({ projectStatusIds, leadStatusIds })
  }

  const statusSelectValues = [
    ...filters.projectStatusIds.map((id) => `project:${id}`),
    ...filters.leadStatusIds.map((id) => `lead:${id}`)
  ]

  const handleProjectTypeChange = (values: string[]) => {
    patchFilters({ projectTypeIds: values })
  }

  // "Appointment" — not a facet (no option list the backend advertises,
  // unlike Salesperson/Status/etc.), just a single fixed choice over the
  // same boolean the KPI row's own Appointments tile already reads
  // (`has_upcoming_appointment`) — reusing `FacetMultiSelectFilter` with a
  // synthetic one-item "facet" keeps this filter's look identical to every
  // other picker in the row ("every filter behaves the same way", this
  // file's own module doc) without a second, bespoke boolean-toggle
  // component. `count: 0` (not a real backend count) intentionally renders
  // no "(N)" suffix — `FacetMultiSelectFilter`'s own falsy-count branch.
  const appointmentOptions = [
    {
      value: "future",
      label: t("globalMap.filters.futureAppointments"),
      count: 0
    }
  ]
  const appointmentSelectValues = filters.hasUpcomingAppointment
    ? ["future"]
    : []
  const handleAppointmentChange = (values: string[]) => {
    patchFilters({ hasUpcomingAppointment: values.includes("future") })
  }

  // Set-equality, not array-order equality — `activeLayers`/`DEFAULT_LAYERS`
  // are both small, order doesn't carry meaning, and comparing by order
  // would treat `["lead","project"]` as "narrowed" from `["project","lead"]`
  // even though every layer is still active.
  const isLayersNarrowed =
    activeLayers.length !== DEFAULT_LAYERS.length ||
    !DEFAULT_LAYERS.every((layer) => activeLayers.includes(layer))

  return (
    // One toolbar row from 1712px up; below that the row wraps rather than
    // compressing the labels past the point of being readable.
    //
    // 1712 is measured, not chosen for roundness. The app's own sidebar and
    // page padding take 244px, so the toolbar gets `viewport - 244`. Eight
    // pickers need ~130px each to show enough of a label to tell "Project
    // Manager" from "Project Type", which with the 337px action group and the
    // gaps comes to ~1468px of toolbar — a 1712px viewport. Below it the
    // single row truncated two pickers to "Proj…" and two to "S…", so the
    // approved behaviour there is two readable rows. Shared by Map, List and
    // Analytics, which all render this same bar.
    <div className="flex w-full shrink-0 flex-wrap items-center gap-2 min-[1712px]:flex-nowrap">
      {/* `contents` keeps this element (and its group role / test id) in the
          DOM while letting the eight pickers take part in the parent's own
          flex layout. Without it the pickers wrap inside their own box, that
          box fills the width, and the action group is pushed onto a third
          line — the approved design puts the actions at the right-hand end of
          the LAST picker row, not on a row of their own. */}
      <div
        className="contents"
        role="group"
        aria-label={t("globalMap.filters.groupLabel")}
        data-testid="global-map-filter-bar"
      >
        {activeTab === "list" ? (
          <>
            {/* List-tab picker row: Active Period, Salesperson, Project
                Manager, Subcontractor, Team Member, Status, Project Type —
                "More Filters" is gone (Project Manager is inline, same as
                the Map tab row below), Region is gone (removed per explicit
                product direction), and the search box moved out to render
                above the map/list content instead, exactly like the Map
                tab's own `GlobalRecordSearchInput` — all per explicit
                product direction, so this row stays short enough to keep
                Clear All on the same line at normal widths, matching the
                Map tab row's layout. */}
            <PresetDateRangeFilter<GlobalMapFilters>
              presets={NO_ACTIVE_PERIOD_PRESETS}
              preset={null}
              after={filters.activePeriodStart}
              before={filters.activePeriodEnd}
              presetKey="activePeriodStart"
              afterKey="activePeriodStart"
              beforeKey="activePeriodEnd"
              label={t("globalMap.filters.activePeriod")}
              placeholder={t("globalMap.filters.activePeriodPlaceholder")}
              allTimeLabel={t("globalMap.filters.activePeriodAllTime")}
              customLabel={t("globalMap.filters.activePeriodCustomRange")}
              onChange={patchFilters}
              className={cn(ACTIVE_PERIOD_WIDTH_CLASS, FILTER_PILL_CLASS)}
            />

            <FacetMultiSelectFilter
              label={t("globalMap.filters.salesperson")}
              options={facets?.salesperson ?? []}
              values={filters.salespersonIds}
              onChange={(values) => patchFilters({ salespersonIds: values })}
              icon={<Icon type="User" />}
            />
            <FacetMultiSelectFilter
              label={t("globalMap.filters.projectManager")}
              options={facets?.projectManager ?? []}
              values={filters.projectManagerIds}
              onChange={(values) => patchFilters({ projectManagerIds: values })}
              icon={<Icon type="Filter" />}
            />
            <FacetMultiSelectFilter
              label={t("globalMap.filters.subcontractor")}
              options={facets?.subcontractor ?? []}
              values={filters.subcontractorIds}
              onChange={(values) => patchFilters({ subcontractorIds: values })}
              icon={<Icon type="Subcontractors" />}
            />
            <FacetMultiSelectFilter
              label={t("globalMap.filters.teamMember")}
              options={facets?.teamMember ?? []}
              values={filters.teamMemberIds}
              onChange={(values) => patchFilters({ teamMemberIds: values })}
              icon={<Icon type="Users" />}
            />

            <FacetMultiSelectFilter
              label={t("globalMap.filters.status")}
              options={facets?.status ?? []}
              values={statusSelectValues}
              onChange={handleStatusChange}
              icon={<Icon type="Filter" />}
            />

            <FacetMultiSelectFilter
              label={t("globalMap.filters.projectType")}
              options={facets?.projectType ?? []}
              values={filters.projectTypeIds}
              onChange={handleProjectTypeChange}
              icon={<Icon type="Projects" />}
            />

            <FacetMultiSelectFilter
              label={t("globalMap.filters.appointment")}
              options={appointmentOptions}
              values={appointmentSelectValues}
              onChange={handleAppointmentChange}
              icon={<Icon type="Calendar" />}
            />
          </>
        ) : (
          <>
            {/* Map tab picker row — untouched by Task 7, byte-for-byte what
                was here before: Active Period, Salesperson, Project Manager,
                Team Member, Subcontractor, Status, Project Type. No Region,
                no Search, no More Filters. */}
            <PresetDateRangeFilter<GlobalMapFilters>
              presets={NO_ACTIVE_PERIOD_PRESETS}
              preset={null}
              after={filters.activePeriodStart}
              before={filters.activePeriodEnd}
              presetKey="activePeriodStart"
              afterKey="activePeriodStart"
              beforeKey="activePeriodEnd"
              label={t("globalMap.filters.activePeriod")}
              placeholder={t("globalMap.filters.activePeriodPlaceholder")}
              allTimeLabel={t("globalMap.filters.activePeriodAllTime")}
              customLabel={t("globalMap.filters.activePeriodCustomRange")}
              onChange={patchFilters}
              className={cn(ACTIVE_PERIOD_WIDTH_CLASS, FILTER_PILL_CLASS)}
            />

            <FacetMultiSelectFilter
              label={t("globalMap.filters.salesperson")}
              options={facets?.salesperson ?? []}
              values={filters.salespersonIds}
              onChange={(values) => patchFilters({ salespersonIds: values })}
              icon={<Icon type="User" />}
            />
            <FacetMultiSelectFilter
              label={t("globalMap.filters.projectManager")}
              options={facets?.projectManager ?? []}
              values={filters.projectManagerIds}
              onChange={(values) => patchFilters({ projectManagerIds: values })}
              icon={<Icon type="Filter" />}
            />
            <FacetMultiSelectFilter
              label={t("globalMap.filters.teamMember")}
              options={facets?.teamMember ?? []}
              values={filters.teamMemberIds}
              onChange={(values) => patchFilters({ teamMemberIds: values })}
              icon={<Icon type="Users" />}
            />
            <FacetMultiSelectFilter
              label={t("globalMap.filters.subcontractor")}
              options={facets?.subcontractor ?? []}
              values={filters.subcontractorIds}
              onChange={(values) => patchFilters({ subcontractorIds: values })}
              icon={<Icon type="Subcontractors" />}
            />

            <FacetMultiSelectFilter
              label={t("globalMap.filters.status")}
              options={facets?.status ?? []}
              values={statusSelectValues}
              onChange={handleStatusChange}
              icon={<Icon type="Filter" />}
            />

            <FacetMultiSelectFilter
              label={t("globalMap.filters.projectType")}
              options={facets?.projectType ?? []}
              values={filters.projectTypeIds}
              onChange={handleProjectTypeChange}
              icon={<Icon type="Projects" />}
            />

            <FacetMultiSelectFilter
              label={t("globalMap.filters.appointment")}
              options={appointmentOptions}
              values={appointmentSelectValues}
              onChange={handleAppointmentChange}
              icon={<Icon type="Calendar" />}
            />
          </>
        )}
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-2">
        {/* "Look Around a Zip Code" — sits immediately left of Clear All, on
            BOTH tabs (this Flex isn't tab-conditional the way the picker
            row above is) — per explicit product direction, moved here from
            its earlier spot beside the Map/List/Analytics tabs
            (`GlobalMapToolbar`'s row 1). It's a real narrowing filter like
            Status or Region, not a map-only layer control, so it belongs
            beside Clear All rather than beside the tab list. */}
        <LookAroundControl />

        <Button
          icon={<EraserIcon />}
          // "Projects & Leads" lives in `activeLayers` (`committedQuery.layers`),
          // not `committedQuery.filters` — `hasAnyActiveFilter`/`clearFilters`
          // alone never see it, so narrowing only that control (no other
          // filter set) used to leave Clear All disabled and, when it WAS
          // enabled for some other reason, clicking it left the layer
          // narrowing in place. `searchTerm` joins the same check for the
          // identical reason — it also lives outside `committedQuery.filters`
          // (see `GlobalMapCommittedQuery`'s own comment), so a lone search
          // with no other filter set must also enable this button, now that
          // clicking it clears search too.
          disabled={
            !hasAnyActiveFilter(filters) && !isLayersNarrowed && !searchTerm
          }
          onClick={() => {
            // The List tab's own entity sub-tab (Lead/Project/All) has no
            // Map-tab equivalent — `activeTab === "list"` is the correct
            // guard here, not just a convenient one. Task 11 — also clears
            // the List tab's own row selection, since a Clear All is a
            // genuinely fresh List query the same way a filters/search/
            // sub-tab change is (`global-map.slice.ts`'s own comments on
            // `setFilters`/`setListEntity` etc.). `clearAllFilters` (Task 14)
            // is the one shared implementation of this dispatch sequence —
            // `ListTab.tsx`'s own empty-state "Clear Filters" button calls
            // the same function, rather than each re-listing the same
            // dispatches independently.
            clearAllFilters(dispatch, activeLayers, {
              includeListReset: activeTab === "list"
            })
            onClearSearch?.()
          }}
          // Tertiary: the quietest control in the row, so it never competes
          // with View All for the eye.
          className="h-10 rounded-lg !px-3 border-[#E2E8F0] bg-white text-[13px] font-medium text-slate-700 !shadow-[0_2px_6px_rgba(15,23,42,0.06)] hover:!border-slate-300 hover:!text-slate-900"
        >
          {t("globalMap.filters.clearAll")}
        </Button>

        {/* List-tab-only: "View All" has no meaning once the List tab has
            replaced it with the entity sub-tabs/Clear All above — the Map
            tab keeps this button, and its `onClick`, completely unchanged. */}
        {activeTab !== "list" && (
          <Button
            type="primary"
            icon={<Icon type="Map" />}
            // Always enabled (never disabled the way Clear All is) — "show me
            // everything" is a meaningful action even with no filter active,
            // e.g. after panning/zooming away from the auto-fit view.
            onClick={() => {
              dispatch(clearFilters())
              if (isLayersNarrowed) dispatch(setActiveLayers(DEFAULT_LAYERS))
              onViewAll?.()
            }}
            // The row's one primary action — the theme's teal fill, with the
            // only real elevation of the three buttons.
            className="h-10 rounded-lg !px-3 text-[13px] font-medium !shadow-[0_4px_12px_rgba(42,125,140,0.25)] hover:!shadow-[0_6px_16px_rgba(42,125,140,0.32)]"
          >
            {t("globalMap.filters.viewAll")}
          </Button>
        )}
      </div>
    </div>
  )
}

export default GlobalMapFilterBar
